import { useState, useEffect } from 'react';
import { Repeat, ChevronDown } from 'lucide-react';

const FREQS = [
  { value: 'daily',    label: 'Daily' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly',  label: 'Monthly' },
  { value: 'custom',   label: 'Custom' },
];

const DAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

/**
 * RecurrencePicker — Q29
 *
 * Props:
 *  - value (object | null) — current recurrence rule
 *  - onChange (rule | null) — emits the new rule
 *  - seedDate (string, "YYYY-MM-DD") — the plan's first date, used to default
 *    the day-of-week pill for weekly/biweekly.
 */
export default function RecurrencePicker({ value, onChange, seedDate }) {
  const enabled = !!(value && value.frequency);
  const [draft, setDraft] = useState(value || null);

  // Keep draft in sync with parent
  useEffect(() => { setDraft(value || null); }, [value]);

  const set = (patch) => {
    const next = { ...(draft || {}), ...patch };
    setDraft(next);
    onChange?.(next);
  };

  const toggleOn = () => {
    if (enabled) {
      onChange?.(null); setDraft(null); return;
    }
    // Default to Weekly, using the seed date's weekday
    const seedDow = seedDate ? new Date(seedDate).getDay() : 1;
    const monBased = (seedDow + 6) % 7; // JS Sun=0..Sat=6 → Mon=0..Sun=6
    const rule = {
      frequency: 'weekly',
      days_of_week: [monBased],
      end_type: 'never',
    };
    setDraft(rule);
    onChange?.(rule);
  };

  return (
    <div data-testid="recurrence-picker">
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="flex items-center gap-2 text-xs font-medium text-[#6B6E73] uppercase tracking-wider">
          <Repeat size={13} /> Repeat
        </label>
        <button
          type="button"
          data-testid="recurrence-toggle"
          aria-pressed={enabled}
          onClick={toggleOn}
          className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#B6E600]' : 'bg-[#E3E3DA]'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      {!enabled ? (
        <p className="text-[11px] text-[#9B9EA3] leading-relaxed">
          One-time event. Toggle on to make this a recurring plan (great for clubs,
          weekly meetups, and workout groups).
        </p>
      ) : (
        <div className="space-y-3 bg-white rounded-2xl border border-black/[0.06] p-3" data-testid="recurrence-form">
          {/* Frequency */}
          <div>
            <p className="text-[10px] font-bold text-[#6B6E73] uppercase tracking-wider mb-1.5">Frequency</p>
            <div className="flex flex-wrap gap-1.5">
              {FREQS.map(f => (
                <button
                  key={f.value}
                  type="button"
                  data-testid={`recurrence-freq-${f.value}`}
                  onClick={() => set({ frequency: f.value })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${draft?.frequency === f.value ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Days — shown for weekly/biweekly/custom */}
          {['weekly', 'biweekly', 'custom'].includes(draft?.frequency) && (
            <div>
              <p className="text-[10px] font-bold text-[#6B6E73] uppercase tracking-wider mb-1.5">Days</p>
              <div className="flex flex-wrap gap-1.5" data-testid="recurrence-days">
                {DAYS.map(d => {
                  const picked = (draft?.days_of_week || []).includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      data-testid={`recurrence-day-${d.value}`}
                      aria-pressed={picked}
                      onClick={() => {
                        const cur = draft?.days_of_week || [];
                        set({
                          days_of_week: picked
                            ? cur.filter(x => x !== d.value)
                            : [...cur, d.value].sort(),
                        });
                      }}
                      className={`w-10 h-9 rounded-xl text-xs font-bold ${picked ? 'bg-[#B6E600] text-[#111]' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ends */}
          <div>
            <p className="text-[10px] font-bold text-[#6B6E73] uppercase tracking-wider mb-1.5">Ends</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: 'never',       lbl: 'Never' },
                { v: 'after_count', lbl: 'After N' },
                { v: 'on_date',     lbl: 'On date' },
              ].map(o => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`recurrence-end-${o.v}`}
                  onClick={() => set({ end_type: o.v })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${(draft?.end_type || 'never') === o.v ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
                >
                  {o.lbl}
                </button>
              ))}
            </div>

            {draft?.end_type === 'after_count' && (
              <input
                data-testid="recurrence-end-count"
                type="number"
                min="1" max="52"
                value={draft.end_count || 12}
                onChange={e => set({ end_count: parseInt(e.target.value) || 1 })}
                className="mt-2 w-24 bg-[#F1F0EA] text-[#111] rounded-xl px-3 py-2 text-sm border border-black/[0.06] focus:outline-none"
                aria-label="Occurrence count"
              />
            )}

            {draft?.end_type === 'on_date' && (
              <input
                data-testid="recurrence-end-date"
                type="date"
                value={draft.end_date || ''}
                onChange={e => set({ end_date: e.target.value })}
                className="mt-2 bg-[#F1F0EA] text-[#111] rounded-xl px-3 py-2 text-sm border border-black/[0.06] focus:outline-none"
                aria-label="End date"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** RecurringBadge — small 🔁 chip used in PlanCard, PlanDetails, Calendar. */
export function RecurringBadge({ plan, compact = false }) {
  if (!plan?.recurrence?.frequency) return null;
  const label = {
    daily: 'Daily',
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    monthly: 'Monthly',
    custom: 'Custom',
  }[plan.recurrence.frequency] || 'Recurring';
  return (
    <span
      data-testid="recurring-badge"
      title={`Recurring · ${label}`}
      className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#B6E600]/20 text-[#3F5200] ${compact ? 'px-1.5 py-0.5' : 'px-2 py-0.5'}`}
    >
      <Repeat size={10} strokeWidth={2.6} />
      {compact ? null : label}
    </span>
  );
}
